// Todo: change ready to mail to .every()

import React, { Fragment, useRef, useState, useCallback, useMemo, useDeferredValue } from "react";
import { connect } from "react-redux";
import { mailProActions, pmaActions } from "../../../_store/_actions";
import { GetStateStorage } from "common/storage";
import { MailingHeaderSearchBox } from "../MailPro/components/MailingHeaderSearchBox";
import { propertyUtil } from "../PropertyUtil";
import { withStyles } from '@mui/styles'
import styles from "./styles";
import compose from "recompose/compose";
import { withSnackbar } from "notistack";
import { showErrorNotification, showSuccessNotification } from "common/helpers";
import { LOG_MODULES, LOG_CODE_PREFIX, trackException } from "common/errorTracker";
import { renderLogicUtil } from "../RenderLogicUtil";
import { NotesCameFrom } from "../MailPro/components/Notes/Common/NotesConfig";
import { noteUtils } from "../MailPro/components/Notes/Common/NoteUtils";
import ApproveMailingListDialog from "../MailPro/components/ApproveMailingListDialog/ApproveMailingListDialog";
import { toLowerCase } from "common/utils/format";
import { withRouter } from "common/utils/router.util";
import NavbarFilterChips from "./NavbarFilterChips";
import MailProApprovalSection from "./MailProApprovalSection";

const NavbarFilterBox = (props) => {
  const {
    hoSearch,
    hoFilter,
    hoQuickFilter,
    propertyData,
    mlsAreas,
    currentArea,
    mailProStatus,
    agentID,
    classes,
    enableMailProApprovalProcess,
    pmaIsLoading,
  } = props;

  // Tracks only the in-flight approval API call (spinner while agent waits for approve response).
  // Area-switch loading comes from Redux via pmaIsLoading.
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);

  // Table renders with real propertyData immediately (highest priority).
  // Chips, countHOMap, and search counts are non-urgent — defer them one
  // React pass so they never block the table paint.
  const deferredPropertyData = useDeferredValue(propertyData);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const popoverRef = useRef(null);

  const handleSearch = useCallback((searchValue) => {
    props.updateHOSearch(searchValue);
  }, [props.updateHOSearch]);

  const filterSort = useCallback((e) => {
    props.updateHOFilter(e.target.value);
  }, [props.updateHOFilter]);

  const defaultSort = useCallback(async () => {
    await props.resetProMailHeaderandAllFilters();
  }, [props.resetProMailHeaderandAllFilters]);

  const continueEditingMode = useCallback(() => {
    setShowConfirmationDialog(false);
  }, []);

  const approveMailingListConfirmation = useCallback(() => {
    setShowConfirmationDialog(true);
  }, []);

  const handleNewMailingChipButtons = useCallback((mode) => {
    props.explicitEnableNewMailProInitFn(true);
    props.disableNewMailingForCurrentSessionFn(false);
    props.setNewMailProInitDialogModeFn(mode);
  }, [props.explicitEnableNewMailProInitFn, props.disableNewMailingForCurrentSessionFn, props.setNewMailProInitDialogModeFn]);

  const approvalSwitchStepAreaFn = useCallback(() => {
    const stateData = GetStateStorage();
    const { listgenUserID } = stateData.userData;
    if (enableMailProApprovalProcess) {
      let newAreaData = propertyUtil.getOneUnapprovedAreaData(mlsAreas, currentArea.mlsAreaID);
      if (newAreaData) {
        showSuccessNotification(props, "Area approved. Please wait...");
        setTimeout(() => {
          props.switchStep(0);
          props.changeCurrentArea(newAreaData.areaIndex, newAreaData.area, listgenUserID);
          props.history.push("/pma");
        }, 1000);
      } else {
        showSuccessNotification(props, "All area(s) are approved.");
      }
    }
  }, [enableMailProApprovalProcess, mlsAreas, currentArea]);

  const approveMailingList = useCallback(async () => {
    setIsApprovalLoading(true);
    try {
      const { listgenUserID } = GetStateStorage().userData;
      const { mlsAreaID } = currentArea;

      const isAreaApproved = await new Promise((resolve) =>
        props.approveMailProArea(listgenUserID, mlsAreaID, resolve)
      );

      const unapprovedAreaCount = (propertyUtil.getUnapprovedAreaCount(mlsAreas) || {}).areaCount;

      if (isAreaApproved) {
        props.updateAreaAsApproved(mlsAreas, mlsAreaID);
        setIsApprovalLoading(false);
        setShowConfirmationDialog(false);
        setTimeout(() => {
          unapprovedAreaCount > 1 && approvalSwitchStepAreaFn();
        }, 1000);
      } else {
        showErrorNotification(props, "Unable to approve area. Please try again.");
        setIsApprovalLoading(false);
        setShowConfirmationDialog(false);
      }
    } catch (e) {
      trackException({
        module: LOG_MODULES.MAILPRO,
        code: `${LOG_CODE_PREFIX.MAILPRO}APPROVE_AREA_FAILED`,
        error: e,
        data: { mlsAreaID: (currentArea || {}).mlsAreaID },
      });
      showErrorNotification(props, "Unable to approve area.");
      setIsApprovalLoading(false);
      setShowConfirmationDialog(false);
    }
  }, [currentArea, mlsAreas, approvalSwitchStepAreaFn]);

  const finishedEditingMode = useCallback(() => {
    setShowConfirmationDialog(true);
    approveMailingList();
  }, [approveMailingList]);

  // countHOMap is consumed by both MailingHeaderSearchBox and NavbarFilterChips,
  // so it must be computed here in the common parent rather than inside either child.
  const { countHOMap, shouldAnniversaryCountButtonFlash } = useMemo(() => {
    let agentListCount = 0;
    const actualHomeowners = deferredPropertyData.filter((prop) => {
      if (parseInt(prop.agentID) === 0) return true;
      agentListCount += 1;
      return false;
    });

    const map = {
      all: actualHomeowners.length + agentListCount,
      blocked: 0,
      active: agentListCount,
      delete: 0,
      congrats: 0,
      totalAnniversaries: 0,
      anniversary: 0,
      expired: 0,
      absentee: 0,
      cancelled: 0,
      duplicateMailingAddress: 0,
      homeValuation: 0,
      condoBlocked: 0,
    };
    let shouldAnniversaryCountButtonFlash = false;

    actualHomeowners.forEach((prop) => {
      if (prop.HOQuickFilterData.isBlocked) map.blocked += 1;
      if (prop.HOQuickFilterData.duplicateMailingAddress) map.duplicateMailingAddress += 1;
      if (prop.HOQuickFilterData.isDeleted) map.delete += 1;
      if (prop.HOQuickFilterData.isActive) map.active += 1;
      if (prop.isRequestedForValuation) map.homeValuation += 1;
      if (toLowerCase(prop.changeDetails.blocked) === "blocked condos") map.condoBlocked += 1;
      if (
        prop.HOQuickFilterData.isAnniversary &&
        renderLogicUtil.checkPropertyStatuswrtHOFilterStatus(prop, hoFilter)
      ) {
        map.totalAnniversaries += 1;
        if (
          noteUtils.getAPropertyPendingAnniversaryNoteStatuswrtHOFilter(
            prop,
            NotesCameFrom.Anniversary,
            hoFilter
          )
        ) {
          shouldAnniversaryCountButtonFlash = true;
          map.anniversary += 1;
        }
      }
      if (
        prop.HOQuickFilterData.isAbsentee &&
        renderLogicUtil.checkPropertyStatuswrtHOFilterStatus(prop, hoFilter)
      ) {
        map.absentee += 1;
      }
    });

    return { countHOMap: map, shouldAnniversaryCountButtonFlash };
  }, [deferredPropertyData, hoFilter]);

  const readyToMail = propertyUtil.readyToMailExceptPausedAreas(mlsAreas);

  return (
    <Fragment>
      {showConfirmationDialog && (
        <ApproveMailingListDialog
          approveMailingListFn={finishedEditingMode}
          continueEditingModeFn={continueEditingMode}
          mlsAreas={mlsAreas}
          hoFilter={hoFilter}
          areaName={currentArea && currentArea.mlsNeighborhood}
          {...props}
        />
      )}
      <div className={classes.mailingHeader}>
        <div className={classes.lasttoFirstButtonSectionUpdated}>
          <MailProApprovalSection
            isApprovalLoading={isApprovalLoading}
            isDataLoading={pmaIsLoading}
            enableMailProApprovalProcess={enableMailProApprovalProcess}
            mailProStatus={mailProStatus}
            readyToMail={readyToMail}
            currentArea={currentArea}
            mlsAreas={mlsAreas}
            agentID={agentID}
            isAgentPaused={props.agentData && props.agentData.isAgentPaused}
            classes={classes}
            popoverRef={popoverRef}
            onApproveClick={approveMailingListConfirmation}
          />
        </div>
        <div className={classes.secondBlockedFilterSection}>
          <MailingHeaderSearchBox
            onSearchChange={handleSearch}
            onResetClick={defaultSort}
            onFilterClick={filterSort}
            hoSearch={hoSearch}
            hoFilter={hoFilter}
            showDeleteFilter={currentArea && currentArea.showDeleteButton}
            showDuplicateMailingAddressFilter
            countHOMap={countHOMap}
            updateHOAdvanceFilter={props.updateHOAdvanceFilter}
            updateHOAdvanceFilterOptionData={props.updateHOAdvanceFilterOptionData}
            hoAdvanceFilter={props.hoAdvanceFilter}
            propertyData={propertyData}
            agentData={props.agentData}
            isDataLoading={pmaIsLoading}
          />
        </div>
        <div className={classes.firstToLastBlockedTextSectionUpdated}>
          <NavbarFilterChips
            countHOMap={countHOMap}
            hoQuickFilter={hoQuickFilter}
            shouldAnniversaryCountButtonFlash={shouldAnniversaryCountButtonFlash}
            propertyData={deferredPropertyData}
            hoFilter={hoFilter}
            mlsAreas={mlsAreas}
            currentArea={currentArea}
            agentData={props.agentData}
            isMailProLocked={props.isMailProLocked}
            isLoading={pmaIsLoading}
            classes={classes}
            updateHOQuickFilter={props.updateHOQuickFilter}
            handleNewMailingChipButtons={handleNewMailingChipButtons}
          />
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    mlsAreas: state.pma.mlsAreas,
    hoSearch: state.pma.hoSearch,
    hoFilter: state.pma.hoFilter,
    hoQuickFilter: state.pma.hoQuickFilter,
    hoAdvanceFilter: state.pma.hoAdvanceFilter,
    currentArea: state.pma.currentArea,
    currentPeriod: state.pma.currentPeriod,
    version: state.pma.version,
    mailingVersion: state.pma.mailingVersion,
    pmaIsLoading: state.pma.isLoading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    mailProHeaderSort: (sorted, mailProHeader) =>
      dispatch(pmaActions.mailProHeaderSort(sorted, mailProHeader)),
    updateHOSearch: (searchTerm) => {
      dispatch(pmaActions.updateHOSearch(searchTerm));
    },
    updateHOFilter: (filterStatus) => {
      dispatch(pmaActions.updateHOFilter(filterStatus));
    },
    updateHOQuickFilter: (quickFilter) => {
      dispatch(pmaActions.updateHOQuickFilter(quickFilter));
    },
    updateHOAdvanceFilter: (advanceFilter) => {
      dispatch(pmaActions.updateHOAdvanceFilter(advanceFilter));
    },
    updateHOAdvanceFilterOptionData: (dataJson) => {
      dispatch(pmaActions.updateHOAdvanceFilterOptionData(dataJson));
    },
    resetProMailHeaderandAllFilters: () => {
      dispatch(pmaActions.resetProMailHeaderandAllFilters());
    },
    updateAreaAsApproved: (mlsAreaData, areaID) => {
      dispatch(mailProActions.updateAreaAsApproved(mlsAreaData, areaID));
    },
    approveMailProArea: (listgenUserID, areaID, cb) => {
      dispatch(mailProActions.approveMailProArea(listgenUserID, areaID, cb));
    },
    changeLoading: (status) => {
      dispatch(pmaActions.changeLoading(status));
    },
    updateHomeownerProp: (hoData) =>
      dispatch(pmaActions.updateHomeownerProp(hoData)),
    updateNavbar: (mlsAreas) => dispatch(pmaActions.updateNavbar(mlsAreas)),
    switchAreaWithStep: (area, step) =>
      dispatch(pmaActions.switchAreaWithStep(area, step)),
    switchStep: (step) => dispatch(pmaActions.switchStep(step)),
    explicitEnableNewMailProInitFn: (data) =>
      dispatch(pmaActions.explicitEnableNewMailProInitFn(data)),
    disableNewMailingForCurrentSessionFn: (data) =>
      dispatch(pmaActions.disableNewMailingForCurrentSessionFn(data)),
    setNewMailProInitDialogModeFn: (data) =>
      dispatch(pmaActions.setNewMailProInitDialogModeFn(data)),
  };
};

export default React.memo(connect(
  mapStateToProps,
  mapDispatchToProps
)(compose(withRouter, withStyles(styles), withSnackbar)(NavbarFilterBox)));
